/**
 * ResultsTable Component
 * Displays individual power records with full details
 * Optimized for bail agent workflow
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  FunnelIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { OathSpinner } from '../../../shared/OathUI';
import {
  OathCheckbox,
  OathSortableHeader,
  OathPagination,
  OathSelectionBanner,
  formatCurrency,
  formatDate,
  getPowerStatusColor,
} from '../../../shared/OathTableComponents';
import { SORT_FIELDS } from '../../../../services/oathPowerInquiryService';
import { getDueDate, getDueDateHighlight } from '../utils/dueDate';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500, 1000];

/**
 * Get display amount for a power - uses bondAmount for executed, powerAmount for unexecuted
 * @param {Object} power - Power record
 * @returns {{ amount: number, isUnexecuted: boolean }}
 */
const getDisplayAmount = (power) => {
  // For unexecuted powers (status 'U'), use powerAmount (denomination) since bondAmount is not set
  const isUnexecuted = power.powerStatus === 'U';
  const amount = isUnexecuted ? power.powerAmount : power.bondAmount;
  return { amount, isUnexecuted };
};



/**
 * Mobile card view for a single power
 */
const PowerCard = ({ power, onViewDetails, isSelected, onSelectionChange }) => {
  const { amount, isUnexecuted } = getDisplayAmount(power);
  const due = getDueDate(power);
  const highlight = getDueDateHighlight(due);

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border overflow-hidden shadow-sm hover:shadow-md transition-shadow ${
      isSelected
        ? 'border-primary dark:border-primary-light ring-1 ring-primary/30'
        : 'border-gray-200 dark:border-gray-700'
    }`}>
      {/* Card Header */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <OathCheckbox
              checked={isSelected}
              onChange={(checked) => onSelectionChange(power.powerNo, checked)}
            />
            <button
              onClick={() => onViewDetails(power.powerNo)}
              className="font-mono font-bold text-primary dark:text-primary-light hover:underline truncate"
            >
              {power.powerNo}
            </button>
          </div>
          <span className={`inline-flex items-center justify-center px-2.5 py-1 text-xs font-bold rounded-full text-center ${getPowerStatusColor(power.powerStatus)}`}>
            {power.powerStatusDescription || power.powerStatus}
          </span>
        </div>
      </div>

      {/* Card Body */}
      <div className="p-4 space-y-3">
        {/* Defendant */}
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Defendant</p>
          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
            {power.defendantFullName || (power.defendantFirstName && power.defendantLastName
              ? `${power.defendantFirstName} ${power.defendantLastName}`
              : power.defendantLastName || power.defendantFirstName || '-')}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              {isUnexecuted ? 'Denomination' : 'Bond Amount'}
            </p>
            <p className="text-sm font-bold text-gray-900 dark:text-white">{formatCurrency(amount)}</p>
          </div>
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Execution Date</p>
          <p className="text-sm text-gray-900 dark:text-white">{formatDate(power.executionDate)}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Due Date</p>
          {highlight ? (
            <span className={`inline-block px-2 py-0.5 rounded text-sm ${highlight.cell} ${highlight.text || 'text-gray-900 dark:text-white'}`}>
              {formatDate(due)}
            </span>
          ) : (
            <p className="text-sm text-gray-900 dark:text-white">{formatDate(due)}</p>
          )}
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Court City</p>
          <p className="text-sm text-gray-900 dark:text-white">{power.courtCity?.toUpperCase() || '-'}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">County</p>
          <p className="text-sm text-gray-900 dark:text-white">{power.countyName || '-'}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Case #</p>
          <p className="text-sm text-gray-900 dark:text-white font-mono">{power.caseNumber || '-'}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Executing Agent</p>
          <p className="text-sm text-gray-900 dark:text-white">{power.executingAgent || '-'}</p>
        </div>
      </div>

      {/* Flags */}
      {(power.missing || power.expired || power.wasEverForfeiture || power.wasEverJudgment) && (
        <div className="flex flex-wrap gap-1.5 pt-2 border-t border-gray-100 dark:border-gray-700">
          {power.missing && (
            <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded">Missing</span>
          )}
          {power.expired && (
            <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs rounded">Expired</span>
          )}
          {power.wasEverForfeiture && (
            <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-xs rounded">Forfeiture</span>
          )}
          {power.wasEverJudgment && (
            <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded">Judgment</span>
          )}
        </div>
      )}
    </div>

    {/* Card Footer */}
    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
      <button
        onClick={() => onViewDetails(power.powerNo)}
        className="w-full flex items-center justify-center gap-2 text-sm text-primary dark:text-primary-light hover:underline"
      >
        <EyeIcon className="h-4 w-4" />
        View Details
      </button>
    </div>
  </div>
  );
};

/**
 * Desktop table row
 */
const TableRow = ({ power, isEven, onViewDetails, isSelected, onSelectionChange }) => {
  const { amount, isUnexecuted } = getDisplayAmount(power);
  const due = getDueDate(power);
  const highlight = getDueDateHighlight(due);

  return (
  <tr className={`hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-colors ${
    isEven ? 'bg-gray-50/50 dark:bg-gray-800/50' : ''
  } ${isSelected ? 'bg-primary-50/70 dark:bg-primary-900/20' : ''}`}>
    {/* Checkbox */}
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 w-12">
      <OathCheckbox
        checked={isSelected}
        onChange={(checked) => onSelectionChange(power.powerNo, checked)}
        size="lg"
      />
    </td>
    {/* Power Number */}
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5">
      <button
        onClick={() => onViewDetails(power.powerNo)}
        className="inline-flex items-center gap-1.5 qhd:gap-2 font-mono font-bold text-primary dark:text-primary-light hover:underline text-sm qhd:text-base 4k:text-lg"
        title="View details"
      >
        <EyeIcon className="h-4 w-4 qhd:h-5 qhd:w-5 4k:h-6 4k:w-6" />
        {power.powerNo}
      </button>
    </td>

    {/* Status */}
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-center">
      <span className={`inline-flex items-center justify-center px-2.5 py-1 qhd:px-3 qhd:py-1.5 4k:px-4 4k:py-2 text-xs qhd:text-sm 4k:text-base font-bold rounded-full text-center ${getPowerStatusColor(power.powerStatus)}`}>
        {power.powerStatusDescription || power.powerStatus}
      </span>
    </td>

    {/* Defendant */}
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5">
      <div className="text-sm qhd:text-base 4k:text-lg font-medium text-gray-900 dark:text-white">
        {power.defendantFirstName && power.defendantLastName
          ? `${power.defendantFirstName} ${power.defendantLastName}`
          : power.defendantLastName || power.defendantFirstName || '-'}
      </div>
    </td>

    {/* Bond Amount / Denomination */}
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 text-right">
      <span
        className="text-sm qhd:text-base 4k:text-lg font-bold text-gray-900 dark:text-white"
        title={isUnexecuted ? 'Denomination (unexecuted)' : 'Bond Amount'}
      >
        {formatCurrency(amount)}
        {isUnexecuted && <span className="ml-1 text-xs qhd:text-sm text-gray-400">*</span>}
      </span>
    </td>

    {/* Execution Date */}
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5">
      <span className="text-sm qhd:text-base 4k:text-lg text-gray-700 dark:text-gray-300">
        {formatDate(power.executionDate)}
      </span>
    </td>

    {/* Due Date */}
    <td className={`px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5 ${highlight?.cell || ''}`}>
      <span className={`text-sm qhd:text-base 4k:text-lg ${highlight?.text || 'text-gray-700 dark:text-gray-300'}`}>
        {formatDate(due)}
      </span>
    </td>

    {/* Court City */}
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5">
      <span className="text-sm qhd:text-base 4k:text-lg text-gray-700 dark:text-gray-300">
        {power.courtCity?.toUpperCase() || '-'}
      </span>
    </td>

    {/* County */}
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5">
      <span className="text-sm qhd:text-base 4k:text-lg text-gray-700 dark:text-gray-300">
        {power.countyName || '-'}
      </span>
    </td>

    {/* Case Number */}
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5">
      <span className="text-sm qhd:text-base 4k:text-lg text-gray-700 dark:text-gray-300 font-mono">
        {power.caseNumber || '-'}
      </span>
    </td>

    {/* Executing Agent */}
    <td className="px-4 py-3 qhd:px-6 qhd:py-4 4k:px-8 4k:py-5">
      <span className="text-sm qhd:text-base 4k:text-lg text-gray-700 dark:text-gray-300">
        {power.executingAgent || '-'}
      </span>
    </td>
  </tr>
  );
};

/**
 * Sortable column header
 */
const SortableHeader = ({ label, field, currentSort, sortDescending, onSort, align = 'left' }) => {
  const isActive = currentSort?.toLowerCase() === field?.toLowerCase();
  const alignClass = align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : 'justify-start';

  const SortIcon = isActive
    ? (sortDescending ? ChevronDownIcon : ChevronUpIcon)
    : ChevronUpDownIcon;

  return (
    <button
      onClick={() => onSort(field, isActive ? !sortDescending : false)}
      className={`flex items-center gap-1 qhd:gap-2 ${alignClass} w-full text-xs qhd:text-sm 4k:text-base font-bold uppercase tracking-wider transition-colors ${
        isActive
          ? 'text-primary dark:text-primary-light'
          : 'text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary-light'
      }`}
    >
      {label}
      <SortIcon className={`h-4 w-4 qhd:h-5 qhd:w-5 4k:h-6 4k:w-6 ${isActive ? 'text-primary dark:text-primary-light' : 'text-gray-400'}`} />
    </button>
  );
};

/**
 * Results table component
 */
const ResultsTable = ({
  results = [],
  accumulatedResults = [], // For mobile infinite scroll
  totalCount = 0,
  pageNumber = 1,
  pageSize = 25,
  totalPages = 0,
  hasNextPage = false,
  onPageChange,
  onPageSizeChange,
  onLoadMore, // Callback for infinite scroll
  sortBy,
  sortDescending,
  onSortChange,
  onViewPowerDetails,
  isLoading = false,
  isLoadingMore = false, // Loading state for infinite scroll
  isLoadingAggregates = false,
  hasSearched = false,
  error = null,
  enableFixedHeaderFooter = true,
  selectedPowers = new Set(),
  onSelectionChange,
  onSelectAll,
  selectAllMatching = false,
  onSelectAllMatching,
  onBulkDischarge,
  onAddToDischargeQueue,
  onBulkComment,
}) => {
  const tableContainerRef = useRef(null);
  const theadRef = useRef(null);
  const paginationRef = useRef(null);
  const mobileScrollSentinelRef = useRef(null); // Sentinel for infinite scroll
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const [fixedHeaderStyle, setFixedHeaderStyle] = useState({});
  const [columnWidths, setColumnWidths] = useState([]);
  const [isFooterFixed, setIsFooterFixed] = useState(false);
  const [fixedFooterStyle, setFixedFooterStyle] = useState({});
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const prevPaginationRef = useRef({ pageNumber, pageSize, sortBy, sortDescending });

  // Quick filter state - client-side filtering on the current dataset
  const [quickFilter, setQuickFilter] = useState('');
  const quickFilterRef = useRef(null);

  // Clear quick filter when a new search loads results (page 1 = new search)
  const prevResultsLenRef = useRef(results.length);
  useEffect(() => {
    if (pageNumber === 1 && results.length !== prevResultsLenRef.current) {
      setQuickFilter('');
    }
    prevResultsLenRef.current = results.length;
  }, [results.length, pageNumber]);

  // Filter function - matches search term against all visible columns
  const matchesQuickFilter = useCallback((power, term) => {
    const lower = term.toLowerCase();
    const fields = [
      power.powerNo,
      power.powerStatusDescription || power.powerStatus,
      power.defendantFirstName,
      power.defendantLastName,
      power.defendantFullName,
      power.courtCity,
      power.countyName,
      power.caseNumber,
      power.executionDate,
    ];
    // Check string fields
    if (fields.some(f => f && String(f).toLowerCase().includes(lower))) return true;
    // Check bond amount as formatted string (e.g., "10,000" or "10000")
    const { amount } = getDisplayAmount(power);
    if (amount != null && String(amount).includes(term)) return true;
    if (amount != null && formatCurrency(amount).toLowerCase().includes(lower)) return true;
    return false;
  }, []);

  // Filtered results for desktop and mobile
  const filteredResults = useMemo(() => {
    if (!quickFilter.trim()) return results;
    return results.filter(p => matchesQuickFilter(p, quickFilter.trim()));
  }, [results, quickFilter, matchesQuickFilter]);

  // Use accumulated results for mobile, regular results for desktop
  const allMobileResults = accumulatedResults.length > 0 ? accumulatedResults : results;
  const filteredMobileResults = useMemo(() => {
    if (!quickFilter.trim()) return allMobileResults;
    return allMobileResults.filter(p => matchesQuickFilter(p, quickFilter.trim()));
  }, [allMobileResults, quickFilter, matchesQuickFilter]);
  const mobileResults = filteredMobileResults;

  // Scroll to top when page, page size, or sort changes (desktop only)
  // On mobile with infinite scroll, we don't want to scroll to top when loading more
  useEffect(() => {
    const prev = prevPaginationRef.current;
    const hasChanged = prev.pageNumber !== pageNumber ||
      prev.pageSize !== pageSize ||
      prev.sortBy !== sortBy ||
      prev.sortDescending !== sortDescending;

    // Only scroll to top on desktop (lg breakpoint = 1024px)
    // Mobile infinite scroll should maintain scroll position
    const isDesktop = window.innerWidth >= 1024;
    if (hasChanged && hasSearched && isDesktop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    prevPaginationRef.current = { pageNumber, pageSize, sortBy, sortDescending };
  }, [pageNumber, pageSize, sortBy, sortDescending, hasSearched]);

  // Track scroll position to show/hide "back to top" button (mobile only)
  useEffect(() => {
    const handleScroll = () => {
      // Show button after scrolling down 400px
      setShowScrollToTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleViewDetails = (powerNo) => {
    if (onViewPowerDetails) {
      onViewPowerDetails(powerNo);
    }
  };

  // Track when table header scrolls out of view to make it fixed (desktop only)
  // Re-run when results change to ensure refs are available after table renders
  const hasResults = results.length > 0;
  useEffect(() => {
    const thead = theadRef.current;
    const container = tableContainerRef.current;
    if (!thead || !container) return;

    // lg breakpoint = 1024px
    const isDesktop = () => window.innerWidth >= 1024;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only apply fixed positioning on desktop screens
        if (!isDesktop()) {
          setIsHeaderFixed(false);
          return;
        }

        const isOutOfView = !entry.isIntersecting;
        setIsHeaderFixed(isOutOfView);

        // Capture header position and column widths when it becomes fixed
        if (isOutOfView) {
          const containerRect = container.getBoundingClientRect();
          const thCells = thead.querySelectorAll('th');
          const widths = Array.from(thCells).map(th => th.getBoundingClientRect().width);
          setColumnWidths(widths);
          setFixedHeaderStyle({
            position: 'fixed',
            top: '3.5rem', // Below nav bar (56px)
            left: containerRect.left,
            width: containerRect.width,
            zIndex: 20,
          });
        }
      },
      { threshold: 0, rootMargin: '-56px 0px 0px 0px' } // Account for nav bar height
    );

    // Re-evaluate on resize (e.g., when switching between mobile/desktop)
    const handleResize = () => {
      if (!isDesktop()) {
        setIsHeaderFixed(false);
      }
    };

    observer.observe(thead);
    window.addEventListener('resize', handleResize);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [hasResults]);

  // Track when pagination footer is not visible to show fixed footer (desktop only)
  // Re-run when results change to ensure refs are available after table renders
  useEffect(() => {
    const pagination = paginationRef.current;
    const container = tableContainerRef.current;
    if (!pagination || !container) return;

    // lg breakpoint = 1024px
    const isDesktop = () => window.innerWidth >= 1024;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Only apply fixed positioning on desktop screens
        if (!isDesktop()) {
          setIsFooterFixed(false);
          return;
        }

        // Show fixed footer when real footer is NOT visible
        const isVisible = entry.isIntersecting;
        setIsFooterFixed(!isVisible);

        // Capture footer position when it becomes fixed
        if (!isVisible) {
          const containerRect = container.getBoundingClientRect();
          setFixedFooterStyle({
            position: 'fixed',
            bottom: '0',
            left: containerRect.left,
            width: containerRect.width,
            zIndex: 20,
          });
        }
      },
      { threshold: 0 }
    );

    // Re-evaluate on resize (e.g., when switching between mobile/desktop)
    const handleResize = () => {
      if (!isDesktop()) {
        setIsFooterFixed(false);
      }
    };

    observer.observe(pagination);
    window.addEventListener('resize', handleResize);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [hasResults]);

  // Infinite scroll observer for mobile
  // The sentinel is inside lg:hidden div, so it's only visible/observable on mobile
  const loadMoreRef = useRef(onLoadMore);
  loadMoreRef.current = onLoadMore;

  // Track loading state to prevent duplicate calls
  const isLoadingRef = useRef(false);
  isLoadingRef.current = isLoadingMore;

  useEffect(() => {
    const sentinel = mobileScrollSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Use refs to always get the latest values without recreating the observer
        if (entry.isIntersecting && loadMoreRef.current && !isLoadingRef.current) {
          loadMoreRef.current();
        }
      },
      {
        threshold: 0,
        rootMargin: '400px', // Trigger well before reaching the end for seamless loading
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [mobileResults.length]); // Re-observe when results change to handle initial visibility

  // Loading state
  if (isLoading && !results.length) {
    return (
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
        <div className="flex flex-col items-center justify-center">
          <div className="p-4 bg-primary/10 rounded-full">
            <OathSpinner size="lg" className="text-primary" />
          </div>
          <p className="mt-4 text-base font-medium text-gray-700 dark:text-gray-300">Searching powers...</p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">This may take a moment</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border-2 border-red-200 dark:border-red-900/50 p-8">
        <div className="flex flex-col items-center text-center">
          <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-gray-900 dark:text-white">Search Error</h3>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-md">{error}</p>
        </div>
      </div>
    );
  }

  // Not searched yet
  if (!hasSearched) {
    return (
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-dashed border-gray-300 dark:border-gray-600 p-12 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
        <div className="flex flex-col items-center text-center">
          <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-full">
            <MagnifyingGlassIcon className="h-10 w-10 text-gray-400 dark:text-gray-500" />
          </div>
          <h3 className="mt-5 text-lg font-semibold text-gray-900 dark:text-white">Ready to Search</h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
            Select your agencies, enter search criteria, and click Search to find powers.
          </p>
        </div>
      </div>
    );
  }

  // No results
  if (results.length === 0) {
    return (
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
        <div className="flex flex-col items-center text-center">
          <div className="p-4 bg-amber-100 dark:bg-amber-900/30 rounded-full">
            <DocumentTextIcon className="h-10 w-10 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="mt-5 text-lg font-semibold text-gray-900 dark:text-white">No Results Found</h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
            No powers matched your search criteria. Try adjusting your filters or search terms.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Loading overlay for subsequent searches */}
      {isLoading && results.length > 0 && (
        <div className="absolute inset-0 bg-white/70 dark:bg-gray-800/70 flex items-center justify-center z-10 backdrop-blur-sm rounded-xl">
          <div className="flex flex-col items-center">
            <OathSpinner size="lg" />
            <p className="mt-3 qhd:mt-4 text-sm qhd:text-base 4k:text-lg font-medium text-gray-600 dark:text-gray-400">Updating results...</p>
          </div>
        </div>
      )}

      {/* Fixed Header - appears when scrolling past original header */}
      {enableFixedHeaderFooter && isHeaderFixed && (
        <div style={fixedHeaderStyle} className="relative bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          {/* Top accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60 z-10" />
          <table className="min-w-full">
            <thead>
              <tr>
                <th style={{ width: columnWidths[0] }} className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 w-12">
                  <OathCheckbox
                    checked={results.length > 0 && results.every(p => selectedPowers.has(p.powerNo))}
                    onChange={(checked) => onSelectAll && onSelectAll(results.map(p => p.powerNo), checked)}
                    size="lg"
                  />
                </th>
                <th style={{ width: columnWidths[1] }} className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                  <OathSortableHeader label="Power #" field={SORT_FIELDS.POWER_NO} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} />
                </th>
                <th style={{ width: columnWidths[2] }} className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-center">
                  <OathSortableHeader label="Status" field={SORT_FIELDS.STATUS} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} align="center" />
                </th>
                <th style={{ width: columnWidths[3] }} className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                  <OathSortableHeader label="Defendant" field={SORT_FIELDS.DEFENDANT} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} tooltip="Sorted by last name" />
                </th>
                <th style={{ width: columnWidths[4] }} className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-right">
                  <OathSortableHeader label="Bond Amount" field={SORT_FIELDS.BOND_AMOUNT} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} align="right" />
                </th>
                <th style={{ width: columnWidths[5] }} className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                  <OathSortableHeader label="Exec Date" field={SORT_FIELDS.EXECUTION_DATE} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} />
                </th>
                <th style={{ width: columnWidths[6] }} className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                  <OathSortableHeader label="Due Date" field={SORT_FIELDS.DUE_DATES} sortField={sortBy} sortDescending={sortDescending} onSort={onSortChange} />
                </th>
                <th style={{ width: columnWidths[7] }} className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                  <SortableHeader label="Court City" field={SORT_FIELDS.COURT_CITY} currentSort={sortBy} sortDescending={sortDescending} onSort={onSortChange} />
                </th>
                <th style={{ width: columnWidths[8] }} className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                  <SortableHeader label="County" field={SORT_FIELDS.COUNTY} currentSort={sortBy} sortDescending={sortDescending} onSort={onSortChange} />
                </th>
                <th style={{ width: columnWidths[9] }} className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                  <SortableHeader label="Case #" field={SORT_FIELDS.CASE_NUMBER} currentSort={sortBy} sortDescending={sortDescending} onSort={onSortChange} />
                </th>
                <th style={{ width: columnWidths[10] }} className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                  <span className="text-xs qhd:text-sm 4k:text-base font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">Executing Agent</span>
                </th>
              </tr>
            </thead>
          </table>
        </div>
      )}

      {/* Desktop Table */}
      <div ref={tableContainerRef} className="relative hidden lg:block overflow-x-auto bg-white dark:bg-gray-800 rounded-t-xl shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60 z-10" />

        {/* Quick Filter Bar */}
        <div className="relative flex items-center gap-3 px-4 py-2.5 qhd:px-6 qhd:py-3 4k:px-8 4k:py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
          <div className="relative flex-1 max-w-sm">
            <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 qhd:h-5 qhd:w-5 text-gray-400 dark:text-gray-500 pointer-events-none" />
            <input
              ref={quickFilterRef}
              type="text"
              value={quickFilter}
              onChange={(e) => setQuickFilter(e.target.value)}
              placeholder="Filter this page..."
              className="w-full pl-9 pr-8 py-1.5 qhd:pl-11 qhd:pr-10 qhd:py-2 4k:pl-12 4k:pr-12 4k:py-2.5 text-sm qhd:text-base 4k:text-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary dark:focus:border-primary-light placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white transition-colors"
            />
            {quickFilter && (
              <button
                onClick={() => { setQuickFilter(''); quickFilterRef.current?.focus(); }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                <XMarkIcon className="h-4 w-4 qhd:h-5 qhd:w-5" />
              </button>
            )}
          </div>
          {quickFilter.trim() && (
            <span className="text-xs qhd:text-sm 4k:text-base text-gray-500 dark:text-gray-400 whitespace-nowrap">
              {filteredResults.length === results.length
                ? `${results.length} results`
                : <><span className="font-semibold text-primary dark:text-primary-light">{filteredResults.length}</span> of {results.length} on this page</>
              }
            </span>
          )}
        </div>

        {/* Selection banner - appears when all page items are selected */}
        <OathSelectionBanner
          selectedCount={selectedPowers.size}
          totalCount={totalCount}
          pageCount={results.length}
          selectAllMatching={selectAllMatching}
          onSelectAllMatching={onSelectAllMatching}
          onClearSelection={() => onSelectAll && onSelectAll(results.map(p => p.powerNo), false)}
        >
          {onBulkDischarge && (
            <button
              onClick={onBulkDischarge}
              className="ml-1 px-3 py-1.5 qhd:px-4 qhd:py-2 bg-primary text-white text-sm qhd:text-base font-medium rounded-lg hover:bg-primary-dark active:scale-95 transition-all shadow-sm"
            >
              Discharge Now
            </button>
          )}
          {onAddToDischargeQueue && (
            <button
              onClick={onAddToDischargeQueue}
              className="ml-1 px-3 py-1.5 qhd:px-4 qhd:py-2 bg-white dark:bg-gray-800 text-primary dark:text-primary-light text-sm qhd:text-base font-medium rounded-lg border border-primary/40 dark:border-primary-light/40 hover:bg-primary-50 dark:hover:bg-primary-900/30 active:scale-95 transition-all shadow-sm"
            >
              Add to Discharge Queue
            </button>
          )}
          {onBulkComment && (
            <button
              onClick={onBulkComment}
              className="ml-1 px-3 py-1.5 qhd:px-4 qhd:py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm qhd:text-base font-medium rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-95 transition-all shadow-sm"
            >
              Add Comment
            </button>
          )}
        </OathSelectionBanner>
        <table className="min-w-full">
          <thead ref={theadRef}>
            <tr className="bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
              <th scope="col" className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 w-12">
                <OathCheckbox
                  checked={results.length > 0 && results.every(p => selectedPowers.has(p.powerNo))}
                  onChange={(checked) => onSelectAll && onSelectAll(results.map(p => p.powerNo), checked)}
                  size="lg"
                />
              </th>
              <th scope="col" className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                <OathSortableHeader
                  label="Power #"
                  field={SORT_FIELDS.POWER_NO}
                  sortField={sortBy}
                  sortDescending={sortDescending}
                  onSort={onSortChange}
                />
              </th>
              <th scope="col" className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-center">
                <OathSortableHeader
                  label="Status"
                  field={SORT_FIELDS.STATUS}
                  sortField={sortBy}
                  sortDescending={sortDescending}
                  onSort={onSortChange}
                  align="center"
                />
              </th>
              <th scope="col" className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                <OathSortableHeader
                  label="Defendant"
                  field={SORT_FIELDS.DEFENDANT}
                  sortField={sortBy}
                  sortDescending={sortDescending}
                  onSort={onSortChange}
                  tooltip="Sorted by last name"
                />
              </th>
              <th scope="col" className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-right">
                <OathSortableHeader
                  label="Bond Amount"
                  field={SORT_FIELDS.BOND_AMOUNT}
                  sortField={sortBy}
                  sortDescending={sortDescending}
                  onSort={onSortChange}
                  align="right"
                />
              </th>
              <th scope="col" className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                <OathSortableHeader
                  label="Exec Date"
                  field={SORT_FIELDS.EXECUTION_DATE}
                  sortField={sortBy}
                  sortDescending={sortDescending}
                  onSort={onSortChange}
                />
              </th>
              <th scope="col" className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                <OathSortableHeader
                  label="Due Date"
                  field={SORT_FIELDS.DUE_DATES}
                  sortField={sortBy}
                  sortDescending={sortDescending}
                  onSort={onSortChange}
                />
              </th>
              <th scope="col" className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                <SortableHeader
                  label="Court City"
                  field={SORT_FIELDS.COURT_CITY}
                  currentSort={sortBy}
                  sortDescending={sortDescending}
                  onSort={onSortChange}
                />
              </th>
              <th scope="col" className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                <SortableHeader
                  label="County"
                  field={SORT_FIELDS.COUNTY}
                  currentSort={sortBy}
                  sortDescending={sortDescending}
                  onSort={onSortChange}
                />
              </th>
              <th scope="col" className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                <SortableHeader
                  label="Case #"
                  field={SORT_FIELDS.CASE_NUMBER}
                  currentSort={sortBy}
                  sortDescending={sortDescending}
                  onSort={onSortChange}
                />
              </th>
              <th scope="col" className="px-4 py-4 qhd:px-6 qhd:py-5 4k:px-8 4k:py-6 text-left">
                <span className="text-xs qhd:text-sm 4k:text-base font-bold uppercase tracking-wider text-gray-600 dark:text-gray-300">Executing Agent</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
            {filteredResults.map((power, index) => (
              <TableRow
                key={power.powerNo || index}
                power={power}
                isEven={index % 2 === 1}
                onViewDetails={handleViewDetails}
                isSelected={selectedPowers.has(power.powerNo)}
                onSelectionChange={onSelectionChange}
              />
            ))}
            {quickFilter.trim() && filteredResults.length === 0 && (
              <tr>
                <td colSpan={11} className="px-4 py-8 qhd:py-10 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <FunnelIcon className="h-8 w-8 qhd:h-10 qhd:w-10 text-gray-300 dark:text-gray-600" />
                    <p className="text-sm qhd:text-base text-gray-500 dark:text-gray-400">
                      No results match "<span className="font-medium text-gray-700 dark:text-gray-300">{quickFilter}</span>" on this page
                    </p>
                    <button
                      onClick={() => { setQuickFilter(''); quickFilterRef.current?.focus(); }}
                      className="text-sm qhd:text-base text-primary dark:text-primary-light hover:underline"
                    >
                      Clear filter
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile/Tablet Card View with Infinite Scroll */}
      <div className="relative lg:hidden bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60 rounded-t-xl" />
        {/* Selection banner - appears when all loaded items are selected */}
        <OathSelectionBanner
          selectedCount={selectedPowers.size}
          totalCount={totalCount}
          pageCount={mobileResults.length}
          selectAllMatching={selectAllMatching}
          onSelectAllMatching={onSelectAllMatching}
          onClearSelection={() => onSelectAll && onSelectAll(mobileResults.map(p => p.powerNo), false)}
        >
          {onBulkDischarge && (
            <button
              onClick={onBulkDischarge}
              className="ml-1 px-3 py-1.5 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary-dark active:scale-95 transition-all shadow-sm"
            >
              Discharge Now
            </button>
          )}
          {onAddToDischargeQueue && (
            <button
              onClick={onAddToDischargeQueue}
              className="ml-1 px-3 py-1.5 bg-white dark:bg-gray-800 text-primary dark:text-primary-light text-sm font-medium rounded-lg border border-primary/40 dark:border-primary-light/40 hover:bg-primary-50 dark:hover:bg-primary-900/30 active:scale-95 transition-all shadow-sm"
            >
              Add to Queue
            </button>
          )}
          {onBulkComment && (
            <button
              onClick={onBulkComment}
              className="ml-1 px-3 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 active:scale-95 transition-all shadow-sm"
            >
              Comment
            </button>
          )}
        </OathSelectionBanner>

        {/* Mobile Quick Filter */}
        <div className="px-4 pt-3 pb-1">
          <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
            <input
              type="text"
              value={quickFilter}
              onChange={(e) => setQuickFilter(e.target.value)}
              placeholder="Filter this page..."
              className="w-full pl-9 pr-8 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary dark:focus:border-primary-light placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white transition-colors"
            />
            {quickFilter && (
              <button
                onClick={() => setQuickFilter('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
          {quickFilter.trim() && (
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              Showing <span className="font-semibold text-primary dark:text-primary-light">{mobileResults.length}</span> of {allMobileResults.length}
            </p>
          )}
        </div>

        {/* Power Cards */}
        <div className="p-4 space-y-4">
        {mobileResults.map((power, index) => (
          <PowerCard
            key={`${power.powerNo}-${index}`}
            power={power}
            onViewDetails={handleViewDetails}
            isSelected={selectedPowers.has(power.powerNo)}
            onSelectionChange={onSelectionChange}
          />
        ))}

        {/* Quick filter empty state */}
        {quickFilter.trim() && mobileResults.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-8">
            <FunnelIcon className="h-8 w-8 text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No results match "<span className="font-medium text-gray-700 dark:text-gray-300">{quickFilter}</span>"
            </p>
            <button
              onClick={() => setQuickFilter('')}
              className="text-sm text-primary dark:text-primary-light hover:underline"
            >
              Clear filter
            </button>
          </div>
        )}

        {/* Infinite scroll sentinel and loading indicator */}
        <div ref={mobileScrollSentinelRef} className="py-2">
          {isLoadingMore && (
            <div className="flex flex-col items-center justify-center py-4">
              <OathSpinner size="md" />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading more...</p>
            </div>
          )}
          {!hasNextPage && mobileResults.length > 0 && !isLoadingMore && (
            <p className="text-center text-sm text-gray-400 dark:text-gray-500 py-2">
              All {totalCount.toLocaleString()} results loaded
            </p>
          )}
        </div>
        </div>
      </div>

      {/* Mobile "Back to Top" floating button */}
      {showScrollToTop && (
        <button
          onClick={scrollToTop}
          className="lg:hidden fixed bottom-6 right-6 z-40 p-3 bg-primary text-white rounded-full shadow-lg shadow-primary/30 hover:bg-primary-dark active:scale-95 transition-all"
          aria-label="Scroll to top"
        >
          <ChevronUpIcon className="h-6 w-6" />
        </button>
      )}

      {/* Fixed Footer Clone - appears when pagination is not visible */}
      {enableFixedHeaderFooter && isFooterFixed && (totalCount > 0 || isLoadingAggregates) && (
        <div style={fixedFooterStyle} className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-[0_-2px_10px_rgba(0,0,0,0.1)]">
          <OathPagination
            currentPage={pageNumber}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            isLoading={isLoading}
            isLoadingAggregates={isLoadingAggregates}
          />
        </div>
      )}

      {/* Pagination - desktop */}
      <div ref={paginationRef} className="hidden lg:block border-x border-b border-gray-200 dark:border-gray-700 rounded-b-xl overflow-hidden">
        {(totalCount > 0 || isLoadingAggregates) && (
          <OathPagination
            currentPage={pageNumber}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            isLoading={isLoading}
            isLoadingAggregates={isLoadingAggregates}
          />
        )}
      </div>

      {/* Mobile Pagination - Hidden when infinite scroll is available */}
      {!onLoadMore && (
        <div className="lg:hidden mt-4">
          {(totalCount > 0 || isLoadingAggregates) && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <OathPagination
                currentPage={pageNumber}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={onPageChange}
                onPageSizeChange={onPageSizeChange}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                isLoading={isLoading}
                isLoadingAggregates={isLoadingAggregates}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ResultsTable;
